import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Terminal, RefreshCw, Database, Package, AlertTriangle, ChevronDown, Trash2, Copy, CreditCard, ShieldAlert } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated set of safe-to-trigger admin operations. Anything destructive is
// flagged with `destructive: true` so the UI requires a typed confirmation
// before firing.
const TAGADA_COMMANDS = [
  {
    id: 'sync-catalog',
    label: 'Sync Catalog Variants → TagadaPay',
    description:
      "Mirrors every Wilvada product + variant into your TagadaPay store and persists the variantId mapping. Skips products already mapped. Run after adding/renaming products. Idempotent.",
    method: 'POST',
    path: '/admin/tagada/sync-catalog',
    icon: Package,
    destructive: false,
  },
  {
    id: 'force-resync-catalog',
    label: 'Force Resync Catalog (Prices)',
    description:
      "Use ONLY when product prices have changed. Wipes the local Wilvada→TagadaPay product mapping and re-creates every product in TagadaPay with current pricing. (TagadaPay's REST API does not allow editing existing variant prices — this is the workaround.) Old TagadaPay products become orphans that you can archive manually from the TagadaPay CRM dashboard. Idempotent: safe to re-run.",
    method: 'POST',
    path: '/admin/tagada/force-resync-catalog',
    icon: Package,
    destructive: true,
    confirmPhrase: 'RECREATE-CATALOG',
  },
  {
    id: 'full-sync',
    label: 'Full Order Sync (Renumber + Cleanup)',
    description:
      "Paginates every PAID TagadaPay order, renumbers matching local orders to TagadaPay's orderNumber, reconciles any missing orders, and DELETES every local order that's not in TagadaPay (including complimentary + pre-TagadaPay Stripe orders). Returns a JSON paper trail. Run once after major migrations.",
    method: 'POST',
    path: '/admin/tagada/full-sync',
    icon: Database,
    destructive: true,
    confirmPhrase: 'DELETE-ORPHAN-ORDERS',
  },
  {
    id: 'reconcile-recent',
    label: 'Reconcile Recent Orders (Last 50)',
    description:
      "Lighter-weight rescue: scans the last 50 TagadaPay orders and creates any local orders that didn't come through the webhook. Use this for one-off paid-order recovery (it never deletes anything).",
    method: 'POST',
    path: '/admin/tagada/reconcile-recent',
    icon: RefreshCw,
    destructive: false,
  },
];

function ResponseViewer({ entry }) {
  const [collapsed, setCollapsed] = useState(false);
  if (!entry) return null;
  const ok = entry.status >= 200 && entry.status < 300;
  return (
    <div
      className={`mt-3 rounded-md border ${ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-red-200 bg-red-50/40'} overflow-hidden`}
      data-testid={`admin-cmd-response-${entry.id}`}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-inherit bg-white/60">
        <div className="flex items-center gap-2 text-xs font-mono">
          <Badge variant={ok ? 'default' : 'destructive'} className={ok ? 'bg-emerald-600 hover:bg-emerald-600' : ''}>
            {entry.status || 'ERR'}
          </Badge>
          <span className="text-slate-600">{entry.method} {entry.path}</span>
          <span className="text-slate-400">· {entry.durationMs}ms</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(entry.body, null, 2));
              toast.success('Copied to clipboard');
            }}
            data-testid="admin-cmd-copy"
          >
            <Copy className="h-3 w-3 mr-1" /> Copy
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setCollapsed((c) => !c)}
          >
            <ChevronDown className={`h-3 w-3 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
          </Button>
        </div>
      </div>
      {!collapsed && (
        <pre
          className="text-[11px] leading-relaxed text-slate-800 font-mono p-3 overflow-auto max-h-96 whitespace-pre-wrap"
          data-testid="admin-cmd-response-body"
        >
{typeof entry.body === 'string' ? entry.body : JSON.stringify(entry.body, null, 2)}
        </pre>
      )}
    </div>
  );
}

function CommandCard({ cmd, onRun, response, loading }) {
  const [confirmText, setConfirmText] = useState('');
  const Icon = cmd.icon || Terminal;
  const canRun = !cmd.destructive || confirmText === cmd.confirmPhrase;
  return (
    <Card data-testid={`admin-cmd-card-${cmd.id}`}>
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className={`h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0 ${cmd.destructive ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-700'}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-heading text-slate-900 flex items-center gap-2">
              {cmd.label}
              {cmd.destructive && (
                <Badge variant="destructive" className="text-[9px] tracking-wider uppercase">
                  <AlertTriangle className="h-2.5 w-2.5 mr-1" /> Destructive
                </Badge>
              )}
            </h3>
            <p className="text-xs text-slate-600 font-body mt-1 leading-relaxed">{cmd.description}</p>
            <code className="block text-[10px] font-mono text-slate-500 mt-2">
              {cmd.method} /api{cmd.path}
            </code>
          </div>
        </div>
        {cmd.destructive && (
          <div className="space-y-2 pl-13">
            <label className="text-[11px] font-medium text-red-700 block">
              Type <code className="bg-red-100 px-1 py-0.5 rounded">{cmd.confirmPhrase}</code> to confirm:
            </label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={cmd.confirmPhrase}
              className="font-mono text-xs h-8"
              data-testid={`admin-cmd-confirm-${cmd.id}`}
            />
          </div>
        )}
        <Button
          onClick={() => { onRun(cmd); if (cmd.destructive) setConfirmText(''); }}
          disabled={!canRun || loading}
          size="sm"
          className={cmd.destructive ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}
          data-testid={`admin-cmd-run-${cmd.id}`}
        >
          {loading ? <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Terminal className="h-3.5 w-3.5 mr-2" />}
          Run
        </Button>
        <ResponseViewer entry={response} />
      </CardContent>
    </Card>
  );
}

export default function AdminToolsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Custom-endpoint terminal — escape hatch for anything not in COMMANDS.
  const [customMethod, setCustomMethod] = useState('POST');
  const [customPath, setCustomPath] = useState('/admin/');
  const [customBody, setCustomBody] = useState('');
  const [customResponse, setCustomResponse] = useState(null);
  const [customLoading, setCustomLoading] = useState(false);

  // Per-command response + loading state, keyed by cmd.id
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState({});

  // Manual Checkout Mode global toggle — when ON, /api/checkout/create-session
  // bypasses TagadaPay entirely and the team emails the customer a payment
  // link out-of-band. Used as a kill-switch fallback.
  const [manualMode, setManualMode] = useState(false);
  const [manualModeLoading, setManualModeLoading] = useState(true);
  const [manualModeSaving, setManualModeSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); toast.error('Admin access required'); return; }
    axios.get(`${API}/admin/settings/manual-checkout`, { withCredentials: true })
      .then((r) => setManualMode(!!r.data?.enabled))
      .catch(() => {})
      .finally(() => setManualModeLoading(false));
  }, [user, authLoading, navigate]);

  const toggleManualMode = async (next) => {
    setManualModeSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/settings/manual-checkout`,
        { enabled: next },
        { withCredentials: true },
      );
      setManualMode(!!data.enabled);
      toast.success(`Manual Checkout Mode ${data.enabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update Manual Checkout Mode');
    } finally {
      setManualModeSaving(false);
    }
  };

  const runCommand = async (cmd) => {
    setLoading((s) => ({ ...s, [cmd.id]: true }));
    const started = performance.now();
    try {
      const url = `${API}${cmd.path}`;
      const res = await axios.request({
        url,
        method: cmd.method,
        withCredentials: true,
        validateStatus: () => true, // never throw — we render any response
      });
      const durationMs = Math.round(performance.now() - started);
      setResponses((s) => ({
        ...s,
        [cmd.id]: { id: cmd.id, method: cmd.method, path: cmd.path, status: res.status, body: res.data, durationMs },
      }));
      if (res.status >= 200 && res.status < 300) {
        toast.success(`${cmd.label} · OK (${durationMs}ms)`);
      } else {
        toast.error(`${cmd.label} · HTTP ${res.status}`);
      }
    } catch (err) {
      const durationMs = Math.round(performance.now() - started);
      setResponses((s) => ({
        ...s,
        [cmd.id]: { id: cmd.id, method: cmd.method, path: cmd.path, status: 0, body: String(err.message || err), durationMs },
      }));
      toast.error(`Network error: ${err.message}`);
    } finally {
      setLoading((s) => ({ ...s, [cmd.id]: false }));
    }
  };

  const runCustom = async () => {
    if (!customPath.startsWith('/')) {
      toast.error("Path must start with '/'");
      return;
    }
    setCustomLoading(true);
    const started = performance.now();
    try {
      let parsedBody;
      if (customBody.trim() && customMethod !== 'GET') {
        try { parsedBody = JSON.parse(customBody); }
        catch { toast.error('Body must be valid JSON'); setCustomLoading(false); return; }
      }
      const url = `${API}${customPath}`;
      const res = await axios.request({
        url,
        method: customMethod,
        data: parsedBody,
        withCredentials: true,
        validateStatus: () => true,
      });
      const durationMs = Math.round(performance.now() - started);
      setCustomResponse({ id: 'custom', method: customMethod, path: customPath, status: res.status, body: res.data, durationMs });
      if (res.status >= 200 && res.status < 300) toast.success(`OK (${durationMs}ms)`);
      else toast.error(`HTTP ${res.status}`);
    } catch (err) {
      const durationMs = Math.round(performance.now() - started);
      setCustomResponse({ id: 'custom', method: customMethod, path: customPath, status: 0, body: String(err.message || err), durationMs });
      toast.error(`Network error: ${err.message}`);
    } finally {
      setCustomLoading(false);
    }
  };

  if (authLoading || !user || user.role !== 'admin') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-muted-foreground animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-tools-page">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-1">Admin</p>
        <h1 className="text-3xl font-heading text-slate-900 flex items-center gap-3">
          <Terminal className="h-7 w-7 text-[hsl(210,45%,40%)]" /> Admin Terminal
        </h1>
        <p className="text-sm text-slate-600 font-body mt-2 max-w-2xl">
          Quick triggers for the admin operations you'd otherwise have to curl. Responses render
          inline below each command — handy as a paper trail. Destructive commands require a
          typed confirmation.
        </p>
      </div>

      {/* ── Checkout Mode (global) ────────────────────────────────────── */}
      <section className="mb-10" data-testid="checkout-mode-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 font-body flex items-center gap-2">
            <CreditCard className="h-3.5 w-3.5" /> Checkout Mode
          </h2>
          <Badge
            variant="outline"
            className={
              manualMode
                ? 'border-amber-300 bg-amber-50 text-amber-800'
                : 'border-emerald-300 bg-emerald-50 text-emerald-800'
            }
            data-testid="manual-checkout-current-mode"
          >
            {manualMode ? 'Manual Mode' : 'TagadaPay (default)'}
          </Badge>
        </div>
        <Card
          className={manualMode ? 'border-amber-300' : ''}
          data-testid="manual-checkout-toggle-card"
        >
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <div className={`h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0 ${manualMode ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>
                  <ShieldAlert className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-heading text-slate-900">
                    Manual Checkout Mode
                  </h3>
                  <p className="text-xs text-slate-600 font-body mt-1 leading-relaxed max-w-xl">
                    Fallback for when the hosted TagadaPay funnel is unavailable.
                    When ON, customer checkout bypasses TagadaPay, creates a local
                    <em> pending </em> order, and emails the buyer that a secure
                    payment link will follow. The customer is NOT charged automatically —
                    your team sends the payment link out-of-band.
                  </p>
                  <code className="block text-[10px] font-mono text-slate-500 mt-2">
                    PATCH /api/admin/settings/manual-checkout
                  </code>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {manualModeSaving && <RefreshCw className="h-4 w-4 text-slate-400 animate-spin" />}
                <Switch
                  checked={manualMode}
                  disabled={manualModeLoading || manualModeSaving}
                  onCheckedChange={(v) => toggleManualMode(!!v)}
                  data-testid="manual-checkout-toggle"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* ── TagadaPay operations ──────────────────────────────────────── */}
      <section className="mb-10" data-testid="tagada-commands-section">
        <h2 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 font-body mb-3 flex items-center gap-2">
          <Database className="h-3.5 w-3.5" /> TagadaPay Operations
        </h2>
        <div className="grid grid-cols-1 gap-4">
          {TAGADA_COMMANDS.map((cmd) => (
            <CommandCard
              key={cmd.id}
              cmd={cmd}
              onRun={runCommand}
              response={responses[cmd.id]}
              loading={!!loading[cmd.id]}
            />
          ))}
        </div>
      </section>

      {/* Custom endpoint escape hatch */}
      <Card className="mt-8" data-testid="admin-cmd-custom-card">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Terminal className="h-4 w-4 text-slate-700" />
            <h3 className="text-sm font-heading text-slate-900">Custom Request</h3>
            <span className="text-[11px] text-slate-500">— for anything not in the list above</span>
          </div>
          <div className="grid grid-cols-12 gap-2 items-start">
            <select
              value={customMethod}
              onChange={(e) => setCustomMethod(e.target.value)}
              className="col-span-3 sm:col-span-2 h-9 rounded-md border border-input bg-background px-2 text-xs font-mono"
              data-testid="admin-cmd-custom-method"
            >
              <option>GET</option>
              <option>POST</option>
              <option>PATCH</option>
              <option>DELETE</option>
              <option>PUT</option>
            </select>
            <Input
              value={customPath}
              onChange={(e) => setCustomPath(e.target.value)}
              placeholder="/admin/..."
              className="col-span-9 sm:col-span-10 h-9 font-mono text-xs"
              data-testid="admin-cmd-custom-path"
            />
          </div>
          {customMethod !== 'GET' && (
            <div>
              <label className="text-[11px] font-medium text-slate-600 block mb-1">JSON body (optional)</label>
              <textarea
                value={customBody}
                onChange={(e) => setCustomBody(e.target.value)}
                placeholder='{ "key": "value" }'
                className="w-full h-24 rounded-md border border-input bg-background p-2 text-xs font-mono"
                data-testid="admin-cmd-custom-body"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={runCustom}
              disabled={customLoading}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="admin-cmd-custom-run"
            >
              {customLoading ? <RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Terminal className="h-3.5 w-3.5 mr-2" />}
              Send
            </Button>
            {customResponse && (
              <Button variant="ghost" size="sm" onClick={() => setCustomResponse(null)}>
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
          <ResponseViewer entry={customResponse} />
        </CardContent>
      </Card>
    </div>
  );
}
